import { FC, useContext } from 'react';

import { gql, useMutation } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  InfoBlock,
  Input,
  Modal,
  NumberInput,
  Select,
} from '@smartshell/ui-kit';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import { getConditionOptions, getRewardOptions } from '../constants';
import { ECondition, EReward } from '../interfaces';
import s from './AchievementManager.module.scss';
import {
  CREATE_ACHIEVEMENT,
  ICreateAchievementRequest,
  ICreateAchievementResponse,
} from './schema';

interface IAchievementManagerProps {
  achievementId?: number | null;
  close: () => void;
}

interface IFormValues {
  name: string;
  conditions: Array<{
    name: ECondition | null;
    value: number | '';
  }>;
  rewards: Array<{
    name: EReward | null;
    value: number | '';
  }>;
}

const DEFAULT_VALUES: IFormValues = {
  name: '',
  conditions: [{ value: '', name: null }],
  rewards: [{ value: '', name: null }],
};

const convertToHours = (value: number): number => value * 60 * 60;

export const AchievementManager: FC<IAchievementManagerProps> = ({ close }) => {
  const { t } = useContext(LocalizationContext);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  const { fields: conditionsFields } = useFieldArray<IFormValues, 'conditions'>(
    {
      control,
      name: 'conditions',
    }
  );
  const { fields: rewardsFields } = useFieldArray<IFormValues, 'rewards'>({
    control,
    name: 'rewards',
  });

  const [createAchievement] = useMutation<
    ICreateAchievementResponse,
    ICreateAchievementRequest
  >(CREATE_ACHIEVEMENT, {
    onCompleted: close,
    update: (cache, { data }) => {
      const achievement = data?.createAchievement;

      if (!achievement) return;

      cache.modify({
        fields: {
          achievements: (existingAchievements = []) => {
            const newAchievement = cache.writeFragment({
              id: cache.identify(achievement),
              data: achievement,
              fragment: gql`
                fragment achievement on Achievement {
                  id
                  conditions {
                    name
                    value
                  }
                  rewards {
                    name
                    value
                  }
                }
              `,
            });

            return [...existingAchievements, newAchievement];
          },
        },
      });
    },
  });

  return (
    <Modal onClose={close}>
      <form
        onSubmit={handleSubmit(({ name, rewards, conditions }) => {
          return createAchievement({
            variables: {
              input: {
                name,
                status: 'ACTIVE',
                conditions: conditions.map((condition) => {
                  if (
                    !condition.name ||
                    condition.name === ECondition.Register
                  ) {
                    return {
                      name: ECondition.Register,
                      value: 0,
                    };
                  }

                  if (condition.name === ECondition.SpentHours) {
                    return {
                      name: condition.name,
                      value: convertToHours(condition.value || 0),
                    };
                  }

                  return {
                    name: condition.name,
                    value: condition.value || 0,
                  };
                }),
                rewards: rewards.map((reward) => ({
                  name: reward.name ?? EReward.None,
                  value: reward.name === EReward.None ? 0 : reward.value || 0,
                })),
              },
            },
          });
        })}
        className={s.form}
      >
        <span className={s.header}>{t('achievements.creation')}</span>
        <InfoBlock message={t('achievement.creation.warning')} />
        <div className={s.fields}>
          <Controller
            render={({ field: { name, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('title')}
                error={error?.message}
                maxLength={24}
              />
            )}
            name="name"
            control={control}
            rules={{
              required: t('errors.required'),
              maxLength: { value: 24, message: t('errors.maxLength', 24) },
            }}
          />
          {conditionsFields.map((conditionField, index) => {
            const fieldValue = watch(`conditions.${index}.name`);

            return (
              <div className={s.group} key={`condition-${conditionField.id}`}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      title={t('achievements.condition')}
                      error={error?.message}
                      options={getConditionOptions(t)}
                    />
                  )}
                  name={`conditions.${index}.name`}
                  defaultValue={conditionField.name}
                  control={control}
                  rules={{ required: t('errors.required') }}
                />
                {fieldValue &&
                  fieldValue !== ECondition.Register &&
                  fieldValue !== ECondition.ClubComment && (
                    <Controller
                      render={({ field, fieldState: { error } }) => (
                        <NumberInput
                          {...field}
                          title={t('value')}
                          error={error?.message}
                        />
                      )}
                      name={`conditions.${index}.value`}
                      defaultValue={conditionField.value}
                      control={control}
                      rules={{
                        required: t('errors.required'),
                        validate: (value) => {
                          if (value < 1) {
                            return t('errors.min', 1);
                          }

                          if (value.toString().split('.')[1]?.length) {
                            return t('errors.integer');
                          }

                          if (fieldValue === ECondition.SpentHours) {
                            if (value > 16000000) {
                              return t('errors.max', '16 000 000');
                            }
                          }
                        },
                      }}
                    />
                  )}
              </div>
            );
          })}
          <BackwardIcon size="s" className={s.arrow} />
          {rewardsFields.map((rewardField, index) => {
            const fieldValue = watch(`rewards.${index}.name`);

            return (
              <div className={s.group} key={`reward-${rewardField.id}`}>
                <Controller
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      title={t('achievements.reward')}
                      error={error?.message}
                      options={getRewardOptions(t)}
                    />
                  )}
                  name={`rewards.${index}.name`}
                  defaultValue={rewardField.name}
                  control={control}
                  rules={{ required: t('errors.required') }}
                />
                {fieldValue && fieldValue !== EReward.None && (
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <NumberInput
                        {...field}
                        title={t('value')}
                        error={error?.message}
                      />
                    )}
                    name={`rewards.${index}.value`}
                    defaultValue={rewardField.value}
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!value) return t('errors.required');

                        if (value < 1) {
                          return t('errors.min', 1);
                        }

                        if (fieldValue === EReward.AddDiscount) {
                          if (value !== parseInt(value.toString())) {
                            return t('errors.integer');
                          }
                          if (value > 100) {
                            return t('errors.max', '100%');
                          }
                        }

                        if (value.toString().split('.')[1]?.length) {
                          return t('errors.integer');
                        }

                        if (value > 16000000) {
                          return t('errors.max', '16 000 000');
                        }

                        return undefined;
                      },
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <Button
          type="submit"
          fullWidth
          isLoading={isSubmitting}
          disabled={!isDirty}
          className={s.button}
        >
          {t('create')}
        </Button>
      </form>
    </Modal>
  );
};
